import { useState, useRef } from "react";
import { FiCamera, FiX, FiSave, FiKey } from "react-icons/fi";
import toast from "react-hot-toast";
import api from "../../utils/api";
import useAuthStore from "../../store/authStore";
import { useSocket } from "../../context/SocketContext";
import { getInitials } from "../../utils/helpers";
import ImageCropModal from "./ImageCropModal";
import styles from "./Modal.module.css";

const toPngBase64 = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 300; canvas.height = 300;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 300, 300);
      ctx.drawImage(img, 0, 0, 300, 300);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = src;
  });

const AVATAR_PRESETS = [
  { id: "p1", url: "https://api.dicebear.com/7.x/adventurer/svg?seed=Felix&backgroundColor=b6e3f4" },
  { id: "p2", url: "https://api.dicebear.com/7.x/adventurer/svg?seed=Mia&backgroundColor=ffdfbf" },
  { id: "p3", url: "https://api.dicebear.com/7.x/adventurer/svg?seed=Sam&backgroundColor=c0aede" },
  { id: "p4", url: "https://api.dicebear.com/7.x/adventurer/svg?seed=Zoe&backgroundColor=ffd5dc" },
  { id: "p5", url: "https://api.dicebear.com/7.x/adventurer/svg?seed=Alex&backgroundColor=d1d4f9" },
  { id: "p6", url: "https://api.dicebear.com/7.x/adventurer/svg?seed=Jordan&backgroundColor=b6e3f4" },
];

const ProfileModal = ({ onClose }) => {
  const { user, updateUser } = useAuthStore();
  const socketRef = useSocket();
  const fileInputRef = useRef(null);

  const [form, setForm]                   = useState({ name: user?.name || "", bio: user?.bio || "" });

  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || "");
  const [pendingBase64, setPendingBase64] = useState(null);
  const [cropSrc, setCropSrc]             = useState(null);
  const [activePreset, setActivePreset]   = useState(null);
  const [isSaving, setIsSaving]           = useState(false);
  const [isSavingPw, setIsSavingPw]       = useState(false);
  const [pwForm, setPwForm]               = useState({ current: "", newPw: "", confirm: "" });
  const [lastSeenPrivacy, setLastSeenPrivacy] = useState(user?.privacy?.lastSeen || "everyone");
  const [twoFAEnabled, setTwoFAEnabled]   = useState(user?.twoFactorEnabled || false);
  const [pwError, setPwError]             = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Image must be under 10MB");
    const reader = new FileReader();
    reader.onloadend = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleCropConfirm = (croppedBase64) => {
    setAvatarPreview(croppedBase64);
    setPendingBase64(croppedBase64);
    setActivePreset(null);
    setCropSrc(null);
  };

  const handlePresetSelect = async (preset) => {
    setActivePreset(preset.id);
    try {
      const png = await toPngBase64(preset.url);
      setAvatarPreview(png);
      setPendingBase64(png);
    } catch {
      toast.error("Failed to load avatar");
    }
  };

  // Save profile info (name, bio, avatar, privacy)
  const handleSaveProfile = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setIsSaving(true);
    try {
      const res = await api.put("/auth/update-profile", {
        name: form.name,
        bio:  form.bio,
        avatar: pendingBase64 || undefined,
        privacy: { lastSeen: lastSeenPrivacy },
      });
      updateUser(res.data.user);
      // Broadcast bio/name/avatar update so contacts see it in real-time
      socketRef?.current?.emit("profile:updated", {
        userId: res.data.user._id,
        name: res.data.user.name,
        bio: res.data.user.bio,
        avatar: res.data.user.avatar,
      });
      toast.success("Profile updated successfully!");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed");
    } finally {
      setIsSaving(false);
    }
  };

  // Save password separately
  const handleChangePassword = async () => {
    if (!pwForm.current) return setPwError("Enter your current password");
    if (pwForm.newPw.length < 6) return setPwError("New password must be 6+ characters");
    if (pwForm.newPw !== pwForm.confirm) return setPwError("Passwords don't match");
    setPwError("");
    setIsSavingPw(true);
    try {
      await api.put("/auth/update-profile", {
        name: user.name, // required field
        currentPassword: pwForm.current,
        newPassword: pwForm.newPw,
      });
      toast.success("🔒 Password changed successfully!");
      setPwForm({ current: "", newPw: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to change password");
    } finally {
      setIsSavingPw(false);
    }
  };

  if (cropSrc) {
    return (
      <ImageCropModal
        src={cropSrc}
        onConfirm={handleCropConfirm}
        onCancel={() => setCropSrc(null)}
      />
    );
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Edit Profile</h3>
          <button className={styles.closeBtn} onClick={onClose}><FiX size={18} /></button>
        </div>

        <div className={styles.scrollBody}>
          {/* Avatar */}
          <div className={styles.avatarSection}>
            <div className={styles.avatarWrapper}>
              <div className={styles.circlePreview}>
                {avatarPreview
                  ? <img src={avatarPreview} alt="avatar" className={styles.circleImg} />
                  : <span className={styles.circleInitials}>{getInitials(form.name)}</span>}
              </div>
              <button className={styles.cameraBtn} onClick={() => fileInputRef.current?.click()}>
                <FiCamera size={14} />
              </button>
            </div>
            <input type="file" ref={fileInputRef} accept="image/*"
              style={{ display: "none" }} onChange={handleFileChange} />
            <p className={styles.avatarHint}>Click camera to upload · drag to crop</p>
          </div>

          {/* Preset avatars */}
          <div className={styles.presetSection}>
            <span className={styles.presetLabel}>Choose avatar</span>
            <div className={styles.presetGrid}>
              {AVATAR_PRESETS.map((p) => (
                <button key={p.id}
                  className={`${styles.presetBtn} ${activePreset === p.id ? styles.presetActive : ""}`}
                  onClick={() => handlePresetSelect(p)}>
                  <img src={p.url} alt="" className={styles.presetImg} />
                </button>
              ))}
            </div>
          </div>

          {/* Name & Bio */}
          <div className={styles.formFields}>
            <div className={styles.field}>
              <label>Display Name</label>
              <input type="text" className="input-field" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name" />
            </div>
            <div className={styles.field}>
              <label>Bio</label>
              <textarea className="input-field" value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="Tell something about yourself..."
                rows={2} style={{ resize: "none" }} maxLength={150} />
              <span style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "right" }}>
                {form.bio.length}/150
              </span>
            </div>
            <div className={styles.field}>
              <label>Email</label>
              <input type="text" className="input-field" value={user?.email} disabled
                style={{ opacity: 0.6, cursor: "not-allowed" }} />
            </div>
          </div>

          {/* Last seen privacy */}
          <div className={styles.privacySection}>
            <div className={styles.privacyRow}>
              <div className={styles.privacyInfo}>
                <span className={styles.privacyTitle}>Last seen</span>
                <span className={styles.privacyDesc}>
                  {lastSeenPrivacy === "everyone"
                    ? "Everyone can see when you were last online"
                    : "Nobody can see your last seen"}
                </span>
              </div>
              <button
                className={`${styles.toggle} ${lastSeenPrivacy === "everyone" ? styles.toggleOn : styles.toggleOff}`}
                onClick={() => setLastSeenPrivacy(p => p === "everyone" ? "nobody" : "everyone")}
              >
                <span className={styles.toggleThumb} />
              </button>
            </div>
          </div>

          {/* 2FA */}
          <div className={styles.privacySection}>
            <div className={styles.privacyRow}>
              <div className={styles.privacyInfo}>
                <span className={styles.privacyTitle}>🔐 Two-Factor Authentication</span>
                <span className={styles.privacyDesc}>
                  {twoFAEnabled ? "Login requires an email OTP code" : "Add an extra layer of security"}
                </span>
              </div>
              <button
                className={`${styles.toggle} ${twoFAEnabled ? styles.toggleOn : styles.toggleOff}`}
                onClick={async () => {
                  try {
                    const res = await api.post("/auth/toggle-2fa");
                    setTwoFAEnabled(res.data.twoFactorEnabled);
                    toast.success(res.data.message);
                  } catch { toast.error("Failed to toggle 2FA"); }
                }}
              >
                <span className={styles.toggleThumb} />
              </button>
            </div>
          </div>
        </div>

        {/* Save profile button */}
        <div className={styles.saveFooter}>
          <button className={styles.saveBtn} onClick={handleSaveProfile} disabled={isSaving}>
            <FiSave size={15} />
            {isSaving ? "Saving..." : "Save Profile"}
          </button>
        </div>


      </div>
    </div>
  );
};

export default ProfileModal;
